<template>
  <!-- Añadir justo antes del header -->
  <nav class="container-fluid fixed-top d-flex justify-content-center">
    <div class="container " style="max-width: 1300px;" >
      <!-- Botón de hamburguesa para móviles -->
      <div class="mobile-menu-toggle d-md-none" @click="toggleMobileMenu">
        <i class="fas" :class="{ 'fa-bars': !mobileMenuOpen, 'fa-times': mobileMenuOpen }"></i>
      </div>
      
      <!-- Logo para móvil (opcional) -->
      <div class="mobile-logo d-md-none">
        <router-link to="/">
          <img src="@/assets/images/LOGO.svg" alt="KAMESEEDS" class="img-fluid" style="height: 100px;">
        </router-link>
      </div>
      
      <!-- Icono de usuario para móviles -->
      <div class="mobile-user-icon d-md-none">
        <router-link class="nav-link user-icon-link" :to="{ path: '/panel' }" :class="{ 'active-link': isRouteActive('/account') }">
          <i class="fas fa-user"></i>
        </router-link>
      </div>
      
      <!-- Menú normal para desktop / Menú desplegable para móvil -->
      <ul class="desktop-menu d-none d-md-flex list-unstyled justify-content-end mb-0 py-3 gap-4 px-lg-2 me-md-5 me-lg-0"
          :class="{ 'mobile-menu-active': mobileMenuOpen }">
        <li class="nav-item d-flex align-items-center">
          <router-link class="nav-link fs-5" :to="{ path: '/' }" 
                       :class="{ 'active-link': isRouteActive('/') }"
                       @click="closeMobileMenu">INICIO</router-link>
        </li>
        <li class="nav-item d-flex align-items-center">
          <router-link class="nav-link fs-5" :to="{ path: '/productos' }" 
                       :class="{ 'active-link': isRouteActive('/productos') }"
                       @click="closeMobileMenu">CATÁLOGO</router-link>
        </li>
        <li class="nav-item d-flex align-items-center">
          <router-link class="nav-link fs-5" :to="{ path: '/blog' }" 
                       :class="{ 'active-link': isRouteActive('/blog') }"
                       @click="closeMobileMenu">BLOG</router-link>
        </li>
        <li v-if="galleryLinkVisible" class="nav-item d-flex align-items-center">
          <router-link class="nav-link fs-5" :to="{ path: '/galeria' }" 
                       :class="{ 'active-link': isRouteActive('/galeria') }"
                       @click="closeMobileMenu">GALERIA</router-link>
        </li>
        <li class="nav-item d-flex align-items-center">
          <router-link class="nav-link fs-5" :to="{ path: '/trofeos' }" 
                       :class="{ 'active-link': isRouteActive('/trofeos') }"
                       @click="closeMobileMenu">TROFEOS</router-link>
        </li>
       
        <li class="nav-item d-flex align-items-center">
          <a class="nav-link fs-5" href="/merch"
                       :class="{ 'active-link': isRouteActive('/merch') }"
                       @click="closeMobileMenu">MERCH</a>
        </li>
        <!-- Icono de usuario para iniciar sesión (solo en desktop) -->
        <li class="nav-item ms-3 d-none d-md-block">
          <router-link class="nav-link user-icon-link" :to="{ path: '/panel' }" :class="{ 'active-link': isRouteActive('/account') }">
            <i class="fas fa-user"></i>
          </router-link>
        </li>
      </ul>
      
      <!-- Menú móvil desplegable -->
      <div class="mobile-menu d-md-none" :class="{ 'open': mobileMenuOpen }">
        <ul class="list-unstyled p-0 m-0">
          <li class="nav-item">
            <router-link class="nav-link" :to="{ path: '/' }" 
                         :class="{ 'active-link': isRouteActive('/') }"
                         @click="closeMobileMenu">INICIO</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" :to="{ path: '/productos' }" 
                         :class="{ 'active-link': isRouteActive('/productos') }"
                         @click="closeMobileMenu">CATÁLOGO</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" :to="{ path: '/blog' }" 
                         :class="{ 'active-link': isRouteActive('/blog') }"
                         @click="closeMobileMenu">BLOG</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" :to="{ path: '/trofeos' }" 
                         :class="{ 'active-link': isRouteActive('/trofeos') }"
                         @click="closeMobileMenu">TROFEOS</router-link>
          </li>
          
          <li class="nav-item">
            <router-link class="nav-link" :to="{ path: '/merch' }" 
                         :class="{ 'active-link': isRouteActive('/merch') }"
                         @click="closeMobileMenu">MERCH</router-link>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import { useGalleryVisibility, loadGalleryData } from '@/services/galleryVisibility';

// Obtenemos la instancia de la ruta actual
const route = useRoute();

/**
 * El link de GALERIA se oculta cuando el panel desactiva la sección:
 * el estado lo publica el backend en /home-gallery/ (`is_active`).
 */
const gallery = useGalleryVisibility();
const galleryLinkVisible = computed(() => gallery.isActive);

onMounted(() => {
  loadGalleryData();
});

// Estado para controlar la visibilidad del menú móvil
const mobileMenuOpen = ref(false);

/**
 * Verifica si la ruta actual coincide con la ruta proporcionada
 * @param {string} path - La ruta a verificar
 * @returns {boolean} - True si la ruta actual coincide con la ruta proporcionada
 */
const isRouteActive = (path) => {
  // Si estamos en la ruta raíz, solo coincide con '/'
  if (path === '/') {
    return route.path === '/';
  }
  // Para otras rutas, verificamos si la ruta actual comienza con la ruta proporcionada
  return route.path.startsWith(path);
};

/**
 * Alterna la visibilidad del menú móvil
 */
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  
  // Si el menú está abierto, evitar el scroll en el body
  if (mobileMenuOpen.value) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
};

/**
 * Cierra el menú móvil
 */
const closeMobileMenu = () => {
  if (mobileMenuOpen.value) {
    mobileMenuOpen.value = false;
    document.body.classList.remove('no-scroll');
  }
};
</script>

<style scoped>
nav {
  background-color: var(--grey-color);
  height: auto;
  min-height: 50px; /* Altura mínima para la navbar */
  z-index: 1000; /* Asegurar que esté por encima del contenido */
}

.active-link {
  color: var(--primary-color) !important;
  font-weight: 600;
}

.nav-link {
  transition: color 0.2s ease;
}

.nav-link:hover {
  color: var(--primary-color);
}

/* Estilos para el icono de usuario */
.user-icon-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--black-color-2);
  transition: all 0.3s ease;
}

.user-icon-link:hover, .user-icon-link.active-link {
  background-color: var(--primary-color);
  color: white !important;
  transform: scale(1.1);
}

.user-icon-link i {
  font-size: 1.2rem;
}

/* Estilos para móvil */
.container {
  position: relative;
}

/* Estilos para la barra de navegación móvil */
@media (max-width: 767.98px) {
  nav .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    position: relative;
  }
  
  .mobile-menu-toggle {
    cursor: pointer;
    z-index: 1010;
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .mobile-logo {
    text-align: center;
    font-weight: bold;
    font-size: 1.2rem;
  }
  
  .mobile-logo a {
    text-decoration: none;
    color: var(--black-color-2);
  }
  
  .mobile-user-icon {
    z-index: 1010;
  }
  
  /* Estilo del menú móvil desplegable */
  .mobile-menu {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--grey-color);
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
    overflow-y: auto;
  }
  
  .mobile-menu.open {
    transform: translateX(0);
  }
  
  .mobile-menu .nav-item {
    margin: 15px 0;
    text-align: center;
  }
  
  .mobile-menu .nav-link {
    font-size: 1.5rem;
    display: block;
    padding: 10px;
  }
  
  /* Previene el scroll cuando el menú está abierto */
  .no-scroll {
    overflow: hidden;
    height: 100%;
  }
}

/* Aseguramos que el menú desktop se muestre correctamente en pantallas grandes */
@media (min-width: 768px) {
  .desktop-menu {
    display: flex !important;
  }
}
</style>
